/**
 * Semantic icon registry for temporary Phosphor fill icons.
 *
 * Screen code should only reference IconId values. Swap a Phosphor asset
 * (or set `override` to a custom PNG under public/) without touching scenes.
 *
 * Catalog verification runs in `npm run icons:sync` (not at runtime) so the
 * game bundle does not ship the full Phosphor catalog.
 */

export type IconWeight = 'fill';

export type IconId =
  | 'nav.hero'
  | 'nav.talents'
  | 'nav.town'
  | 'nav.quests'
  | 'nav.settings'
  | 'hud.portrait'
  | 'hud.class'
  | 'hud.general'
  | 'hud.gold'
  | 'hud.materials'
  | 'hud.settings'
  | 'hud.bell'
  | 'equip.helmet'
  | 'equip.chest'
  | 'equip.belt'
  | 'equip.gloves'
  | 'equip.boots'
  | 'equip.main_hand'
  | 'equip.off_hand'
  | 'equip.ring'
  | 'equip.amulet'
  | 'equip.special_die'
  | 'stat.hp'
  | 'stat.dice'
  | 'stat.shield'
  | 'action.filter'
  | 'action.inspect'
  | 'action.sort'
  | 'action.equip'
  | 'action.unequip'
  | 'action.compare'
  | 'talent.general'
  | 'talent.vanguard'
  | 'talent.berserker'
  | 'talent.skirmisher'
  | 'talent.bulwark'
  | 'talent.locked'
  | 'talent.unlocked'
  | 'talent.available'
  | 'talent.selected'
  | 'talent.points'
  | 'town.dungeon_gate'
  | 'town.training_grounds'
  | 'town.blacksmith'
  | 'town.tavern'
  | 'town.shrine'
  | 'ui.close'
  | 'ui.back'
  | 'ui.confirm'
  | 'ui.cancel'
  | 'ui.info'
  | 'ui.warning'
  | 'ui.locked'
  | 'ui.unavailable'
  | 'status.fire'
  | 'status.poison'
  | 'status.stun'
  | 'status.bleed'
  | 'status.vulnerable'
  | 'combat.back';

export type IconDef = {
  /** Phosphor asset name (catalog `name`, no weight suffix). */
  phosphor: string;
  weight: IconWeight;
  /**
   * Optional custom art path relative to site root (e.g. assets/ui/custom/helmet.png).
   * When set, preload uses this instead of the Phosphor SVG.
   */
  override?: string;
};

/** Verified against @phosphor-icons/core catalog at sync time. */
export const ICON_REGISTRY: Record<IconId, IconDef> = {
  'nav.hero': { phosphor: 'shield', weight: 'fill' },
  'nav.talents': { phosphor: 'tree-structure', weight: 'fill' },
  'nav.town': { phosphor: 'house', weight: 'fill' },
  'nav.quests': { phosphor: 'scroll', weight: 'fill' },
  'nav.settings': { phosphor: 'gear', weight: 'fill' },
  'hud.portrait': { phosphor: 'user-circle', weight: 'fill' },
  'hud.class': { phosphor: 'identification-badge', weight: 'fill' },
  'hud.general': { phosphor: 'star', weight: 'fill' },
  'hud.gold': { phosphor: 'coins', weight: 'fill' },
  'hud.materials': { phosphor: 'cube', weight: 'fill' },
  'hud.settings': { phosphor: 'gear', weight: 'fill' },
  'hud.bell': { phosphor: 'bell', weight: 'fill' },
  'equip.helmet': { phosphor: 'football-helmet', weight: 'fill' },
  'equip.chest': { phosphor: 'shirt-folded', weight: 'fill' },
  'equip.belt': { phosphor: 'belt', weight: 'fill' },
  'equip.gloves': { phosphor: 'hand', weight: 'fill' },
  'equip.boots': { phosphor: 'boot', weight: 'fill' },
  'equip.main_hand': { phosphor: 'sword', weight: 'fill' },
  'equip.off_hand': { phosphor: 'shield-chevron', weight: 'fill' },
  'equip.ring': { phosphor: 'circle', weight: 'fill' },
  'equip.amulet': { phosphor: 'diamond', weight: 'fill' },
  'equip.special_die': { phosphor: 'dice-five', weight: 'fill' },
  'stat.hp': { phosphor: 'heart', weight: 'fill' },
  'stat.dice': { phosphor: 'dice-five', weight: 'fill' },
  'stat.shield': { phosphor: 'shield', weight: 'fill' },
  'action.filter': { phosphor: 'funnel', weight: 'fill' },
  'action.inspect': { phosphor: 'magnifying-glass', weight: 'fill' },
  'action.sort': { phosphor: 'sort-ascending', weight: 'fill' },
  'action.equip': { phosphor: 'hand-deposit', weight: 'fill' },
  'action.unequip': { phosphor: 'hand-withdraw', weight: 'fill' },
  'action.compare': { phosphor: 'columns', weight: 'fill' },
  'talent.general': { phosphor: 'star', weight: 'fill' },
  'talent.vanguard': { phosphor: 'shield-star', weight: 'fill' },
  'talent.berserker': { phosphor: 'axe', weight: 'fill' },
  'talent.skirmisher': { phosphor: 'crosshair', weight: 'fill' },
  'talent.bulwark': { phosphor: 'shield-check', weight: 'fill' },
  'talent.locked': { phosphor: 'lock', weight: 'fill' },
  'talent.unlocked': { phosphor: 'lock-open', weight: 'fill' },
  'talent.available': { phosphor: 'sparkle', weight: 'fill' },
  'talent.selected': { phosphor: 'seal-check', weight: 'fill' },
  'talent.points': { phosphor: 'hexagon', weight: 'fill' },
  'town.dungeon_gate': { phosphor: 'castle-turret', weight: 'fill' },
  'town.training_grounds': { phosphor: 'barbell', weight: 'fill' },
  'town.blacksmith': { phosphor: 'hammer', weight: 'fill' },
  'town.tavern': { phosphor: 'beer-stein', weight: 'fill' },
  'town.shrine': { phosphor: 'church', weight: 'fill' },
  'ui.close': { phosphor: 'x', weight: 'fill' },
  'ui.back': { phosphor: 'arrow-left', weight: 'fill' },
  'ui.confirm': { phosphor: 'check', weight: 'fill' },
  'ui.cancel': { phosphor: 'prohibit', weight: 'fill' },
  'ui.info': { phosphor: 'info', weight: 'fill' },
  'ui.warning': { phosphor: 'warning', weight: 'fill' },
  'ui.locked': { phosphor: 'lock', weight: 'fill' },
  'ui.unavailable': { phosphor: 'prohibit', weight: 'fill' },
  'status.fire': { phosphor: 'flame', weight: 'fill' },
  'status.poison': { phosphor: 'flask', weight: 'fill' },
  'status.stun': { phosphor: 'lightning', weight: 'fill' },
  'status.bleed': { phosphor: 'drop', weight: 'fill' },
  'status.vulnerable': { phosphor: 'crosshair', weight: 'fill' },
  'combat.back': { phosphor: 'arrow-left', weight: 'fill' },
};

export function iconAssetPath(def: IconDef): string {
  if (def.override) {
    return def.override;
  }
  return `assets/icons/phosphor/${def.weight}/${def.phosphor}-${def.weight}.svg`;
}

export function iconTextureKey(id: IconId): string {
  return `icon:${id}`;
}

export const EQUIP_SLOT_ICONS: Record<string, IconId> = {
  helmet: 'equip.helmet',
  chest: 'equip.chest',
  belt: 'equip.belt',
  boots: 'equip.boots',
  main_hand: 'equip.main_hand',
  off_hand: 'equip.off_hand',
  ring_1: 'equip.ring',
  ring_2: 'equip.ring',
  special_die: 'equip.special_die',
  // Legacy wireframe ids (pre-M7-A) — kept so old refs resolve.
  gloves: 'equip.gloves',
  ring: 'equip.ring',
  amulet: 'equip.amulet',
};

export const TOWN_BUILDING_ICONS: Record<string, IconId> = {
  dungeon_gate: 'town.dungeon_gate',
  training_grounds: 'town.training_grounds',
  blacksmith: 'town.blacksmith',
  tavern: 'town.tavern',
  shrine: 'town.shrine',
};

export const NAV_TAB_ICONS: Record<string, IconId> = {
  hero: 'nav.hero',
  talents: 'nav.talents',
  town: 'nav.town',
  quests: 'nav.quests',
  settings: 'nav.settings',
};
